// src/components/tabs/TabPane.tsx
// One tab's page, mounted in a router of its own.
//
// Nesting a router inside the app's BrowserRouter is normally an invariant
// error: Router calls useInRouterContext(), which is `useContext(LocationContext)
// != null`. Providing null for that context resets the check, which is what lets
// each tab keep an independent history — and why pages need no changes at all:
// useNavigate, useParams, useLocation and Link inside them address their own tab.
//
// Pinned to react-router 6.30; the UNSAFE_ prefix is the library's marker for
// internals, so a major upgrade must re-verify this.

import { useEffect, useRef } from 'react';
import {
  MemoryRouter,
  UNSAFE_LocationContext,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { AppRoutes } from '@/routes/AppRoutes';
import { tabsStore } from '@/stores/TabsStore';
import { resolveTitle } from '@/features/tabs/logic';
import type { ContentAlign, ContentWidth } from '@/features/tabs/types';

/**
 * Reports the pane's own location back to the store, so the tab title follows
 * navigation and a reload restores where the user actually was.
 */
const LocationReporter = observer(({ tabId, isFocused }: { tabId: string; isFocused: boolean }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { t } = useI18n();

  useEffect(() => {
    const path = location.pathname + location.search;
    tabsStore.updateTabLocation(tabId, path, resolveTitle(location.pathname, t));

    // Only the focused pane speaks for the address bar; otherwise several
    // mounted pages would fight over it. replaceState keeps the browser's own
    // history out of it — pane history is the pane's business.
    if (isFocused) {
      const current = window.location.pathname + window.location.search;
      if (current !== path) window.history.replaceState(null, '', path);
    }
    void navigationType;
  }, [location.pathname, location.search, navigationType, tabId, isFocused, t]);

  return null;
});

function widthClass(width: ContentWidth, align: ContentAlign): string {
  if (width === 'full') return 'w-full';
  const alignment =
    align === 'left' ? 'mr-auto' : align === 'right' ? 'ml-auto' : 'mx-auto';
  return `w-full max-w-4xl ${alignment}`;
}

interface TabPaneProps {
  tabId: string;
  path: string;
  isActive: boolean;
  isFocused: boolean;
  width: ContentWidth;
  align: ContentAlign;
}

/**
 * An inactive pane is hidden rather than unmounted, which is what keeps scroll
 * position, filters and half-filled forms alive across tab switches.
 */
export const TabPane = observer(
  ({ tabId, path, isActive, isFocused, width, align }: TabPaneProps) => {
    // The router is created once per tab: re-creating it on every path change
    // would throw away the pane's history and its pages' state.
    const initialPath = useRef(path);

    return (
      <div
        className={isActive ? 'flex-1 min-h-0 overflow-y-auto' : 'hidden'}
        data-tab-pane={tabId}
        aria-hidden={!isActive}
      >
        <div className={`${widthClass(width, align)} p-6`}>
          <UNSAFE_LocationContext.Provider value={null as never}>
            <MemoryRouter initialEntries={[initialPath.current]}>
              <LocationReporter tabId={tabId} isFocused={isFocused && isActive} />
              <AppRoutes />
            </MemoryRouter>
          </UNSAFE_LocationContext.Provider>
        </div>
      </div>
    );
  },
);
